import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { featureFlags, ticketPollingIntervalSec } from "../config/featureConfig";
import { useAuth } from "../services/providers/authProvider";
import ticketService from "../services/ticketService";

const TicketUnreadContext = createContext({
  menuBadgeCount: 0,
  ticketSnapshots: [],
  refreshSummary: async () => {},
});

export function TicketUnreadProvider({ children }) {
  const location = useLocation();
  const isImpersonateBootstrap = location.pathname === "/impersonate-login";
  const { token, isImpersonated } = useAuth() || {};
  const [menuBadgeCount, setMenuBadgeCount] = useState(0);
  const [ticketSnapshots, setTicketSnapshots] = useState([]);
  const pollingTimerRef = useRef(null);
  const pollingEnabledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    if (!token || !featureFlags.enableTicketPolling || !featureFlags.enableTicket) {
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      pollingEnabledRef.current = false;
      return;
    }
    try {
      const res = await ticketService.getSummary({ silent: true });
      setMenuBadgeCount(Number(res.menuBadgeCount) || 0);
      setTicketSnapshots(Array.isArray(res.tickets) ? res.tickets : []);
      pollingEnabledRef.current = Boolean(res.pollingEnabled);
    } catch (e) {
      console.warn("ticket summary poll failed", e);
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      pollingEnabledRef.current = false;
    }
  }, [token]);

  useEffect(() => {
    stopPolling();
    if (
      isImpersonateBootstrap
      || !featureFlags.enableTicketPolling
      || !token
      || !featureFlags.enableTicket
    ) {
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      pollingEnabledRef.current = false;
      return undefined;
    }
    let cancelled = false;
    const startPolling = async () => {
      await refreshSummary();
      if (cancelled || isImpersonated || !pollingEnabledRef.current) {
        return;
      }
      const ms = Math.max(1, ticketPollingIntervalSec) * 1000;
      pollingTimerRef.current = setInterval(refreshSummary, ms);
    };
    startPolling();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [isImpersonateBootstrap, token, isImpersonated, refreshSummary, stopPolling]);

  useEffect(() => {
    const handleLogout = () => {
      stopPolling();
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      pollingEnabledRef.current = false;
    };
    globalThis.addEventListener("wcp:auth:logout", handleLogout);
    return () => globalThis.removeEventListener("wcp:auth:logout", handleLogout);
  }, [stopPolling]);

  const value = useMemo(
    () => ({ menuBadgeCount, ticketSnapshots, refreshSummary }),
    [menuBadgeCount, ticketSnapshots, refreshSummary]
  );

  return (
    <TicketUnreadContext.Provider value={value}>
      {children}
    </TicketUnreadContext.Provider>
  );
}

export function useTicketUnread() {
  return useContext(TicketUnreadContext);
}

TicketUnreadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
